import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Calendar } from "lucide-react";
import { demoRequestSchema, DemoRequestFormData } from "@/lib/validations/demo-request";
import { useDemoRequest } from "@/hooks/use-demo-request";
import { hasAnalyticsConsent } from "@/hooks/useCookieConsent";
import SEO from "@/components/marketing/SEO";
import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";

const Contact = () => {
  const navigate = useNavigate();
  const { mutate: submitRequest, isPending } = useDemoRequest();

  const form = useForm<DemoRequestFormData>({
    resolver: zodResolver(demoRequestSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      establishmentName: "",
      establishmentType: "non_renseigne",
      seatingCapacity: undefined,
      city: "",
      interestedPlan: "undecided",
      message: "",
      contactPreference: "any",
      honeypot: "",
    },
  });

  const onSubmit = (data: DemoRequestFormData) => {
    // Track Meta Pixel Lead event (only if consent granted)
    if (typeof window.fbq === 'function' && hasAnalyticsConsent()) {
      window.fbq('track', 'Lead', {
        content_name: 'Demo Request Form',
        content_category: data.interestedPlan,
      });
    }

    submitRequest(
      { ...data, context: "contact-page" },
      {
        onSuccess: () => {
          navigate("/merci");
        },
      }
    );
  };

  return (
    <>
      <SEO
        title="Demande de démo gratuite | Piktable"
        description="Demandez votre premier mois gratuit sans engagement. Remplissez le formulaire et démarrez avec Piktable, la solution de réservation pour restaurants en Suisse romande."
        canonical="/contact"
      />
      <Header />
      <main className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="text-center mb-8">
            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-foreground mb-4">
              Demander votre premier mois gratuit
            </h1>
            <p className="text-muted-foreground text-lg">
              Remplissez ce formulaire et nous vous contacterons rapidement pour démarrer votre essai gratuit.
            </p>
            <p className="text-sm text-muted-foreground mt-4 flex items-center justify-center gap-1">
              <Calendar className="w-4 h-4" />
              Préférez-vous réserver directement un appel ?
              <a 
                href="https://calendly.com/maxdmnt/piktable" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline ml-1"
              >
                Planifier un rendez-vous →
              </a>
            </p>
          </div>

          <div className="bg-card border border-border rounded-xl p-6 md:p-8 shadow-sm">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                {/* Honeypot field - hidden from users, catches bots */}
                <FormField
                  control={form.control}
                  name="honeypot"
                  render={({ field }) => (
                    <FormItem className="hidden">
                      <FormLabel>Leave this field empty</FormLabel>
                      <FormControl>
                        <Input {...field} tabIndex={-1} autoComplete="off" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nom complet *</FormLabel>
                        <FormControl>
                          <Input placeholder="Jean Dupont" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email *</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="jean@exemple.ch" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Téléphone</FormLabel>
                      <FormControl>
                        <Input placeholder="+41 79 123 45 67" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="establishmentName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nom de l'établissement</FormLabel>
                        <FormControl>
                          <Input placeholder="Café du Centre" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="establishmentType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type d'établissement</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionnez" />
                            </SelectTrigger>
                          </FormControl>
                            <SelectContent>
                              <SelectItem value="non_renseigne">Non renseigné</SelectItem>
                              <SelectItem value="cafe">Café</SelectItem>
                              <SelectItem value="restaurant">Restaurant</SelectItem>
                              <SelectItem value="brasserie">Brasserie</SelectItem>
                              <SelectItem value="autre">Autre</SelectItem>
                            </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="seatingCapacity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre de places</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            placeholder="50" 
                            {...field}
                            onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ville</FormLabel>
                        <FormControl>
                          <Input placeholder="Lausanne" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="interestedPlan"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Plan d'intérêt</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez un plan" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="standard">Offre Standard - 49 CHF/mois</SelectItem>
                          <SelectItem value="founders">Offre Printemps 2026 - 29 CHF à vie</SelectItem>
                          <SelectItem value="undecided">Je ne sais pas encore</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message (optionnel)</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Parlez-nous de vos besoins..."
                          className="resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactPreference"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Comment souhaitez-vous être contacté ?</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex flex-col space-y-1"
                        >
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="email" />
                            </FormControl>
                            <FormLabel className="font-normal">
                              Par email
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="phone" />
                            </FormControl>
                            <FormLabel className="font-normal">
                              Par téléphone
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="any" />
                            </FormControl>
                            <FormLabel className="font-normal">
                              Peu importe
                            </FormLabel>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="pt-4">
                  <Button type="submit" disabled={isPending} className="w-full" size="lg">
                    {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Envoyer la demande
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Contact;